import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";

import { connectToApi } from "@api/components/connectToApi";
import { Api } from "@api/index";
import { compose } from "@util/compose";
import { messages } from "../../messages";
import { SalesState } from "../../models";
import {
    getNewReleasesStreamsForPeriod,
    getPlaylistStreamsForPeriod,
    getTotalStreamsForPeriod,
    PeriodProps,
} from "../../selectors";
import { HeadlineStat } from "../HeadlineStat";

interface InnerProps {
    totalStreams: number;
    playlistStreams: number;
    newReleasesStreams: number;
}

interface OuterProps extends PeriodProps {
    period?: React.ReactNode;
}

type Props = OuterProps & InnerProps;

const HeadlineStatsForPeriod = ({
    totalStreams,
    playlistStreams,
    newReleasesStreams,
    period,
}: Props) => {
    const { formatMessage, formatNumber } = useIntl();
    return (
        <div className="headline-stats-period">
            <div className="total-count">
                <HeadlineStat
                    value={totalStreams}
                    label={period == null
                        ? <FormattedMessage {...messages.totalStreamsNoValue} />
                        : <FormattedMessage
                            {...messages.totalStreamsOverPeriod}
                            values={{ period }}
                        />
                    }
                    title={formatMessage(messages.numberStreams, {
                        number: formatNumber(totalStreams),
                        total: totalStreams,
                    })}
                />
            </div>
            <HeadlineStat
                value={playlistStreams}
                totalCount={totalStreams}
                isPercentage
                label={<FormattedMessage {...messages.headlineStatPlaylistsLabel} />}
                title={formatMessage(messages.numberStreams, {
                    number: playlistStreams == null ? "-" : formatNumber(playlistStreams),
                    total: playlistStreams,
                })}
            />
            <HeadlineStat
                value={newReleasesStreams}
                totalCount={totalStreams}
                isPercentage
                label={<FormattedMessage {...messages.headlineStatNewReleasesLabel} />}
                title={formatMessage(messages.numberStreams, {
                    number: newReleasesStreams == null ? "-" : formatNumber(newReleasesStreams),
                    total: newReleasesStreams,
                })}
            />
        </div>
    );
};

const makeMapStateToProps = () => (state: SalesState, props: OuterProps) => ({
    totalStreams: getTotalStreamsForPeriod(state, props),
    playlistStreams: getPlaylistStreamsForPeriod(state, props),
    newReleasesStreams: getNewReleasesStreamsForPeriod(state, props),
} as InnerProps);

const makeGetHeadlineStats = (props: PeriodProps) => () => Api.headlineStatsGet(props);

const ConnectedHeadlineStateForPeriod = compose<InnerProps, OuterProps>(
    connect(makeMapStateToProps, null),
    connectToApi((props: OuterProps) => ({
        showRefresh: false,
        actionCreators: [makeGetHeadlineStats(props)],
    })),
)(HeadlineStatsForPeriod);

export {
    HeadlineStatsForPeriod as HeadlineStatsForPeriodPresenter,
    ConnectedHeadlineStateForPeriod as HeadlineStatsForPeriod,
};
